import { useEffect, useState, useCallback } from 'react';

import { get, isEmpty } from 'ravenjs/utils/lodash';
import _orderBy from 'lodash/orderBy';

import { APP_PERMISSIONS } from 'constants/permissions';
import commentTypes from 'components/Comments/CommentTypeSelect/CommentTypes';
import { COMMENTS } from 'constants/tickets';
import Acl from 'modules/acl';
import { canDeletePACommentsAndResources, canUpdatePACommentsAndResources } from 'utils/tickets';

export function usePreppedAnswerComments({
    createComment,
    currentUserId,
    deleteComment,
    fetchComments,
    fetchUsers,
    isPartner,
    partnerId,
    preppedAnswerCurrentState,
    preppedAnswerId,
    updateComment,
    userId,
}) {
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [comments, setComments] = useState([]);
    const [body, setBody] = useState('');
    const [communicationType, setCommunicationType] = useState(commentTypes.basic);

    const getComments = useCallback(async () => {
        try {
            setLoading(true);

            let commentParams = {};
            if (isEmpty(preppedAnswerId)) {
                commentParams = isPartner
                    ? { 'filter[partner_id]': partnerId }
                    : { 'filter[user_id]': userId };
            } else {
                commentParams = { 'filter[prepped_answer_id]': preppedAnswerId };
            }
            commentParams = {
                ...commentParams,
                sort: '-updated_at',
                paginate: false,
            };

            const commentsResponse = await fetchComments({ ...commentParams });
            let comments = get(commentsResponse, 'data', []);
            // build users map, add commenter field to comments list
            const userIdSet = new Set();
            comments.forEach(({ attributes }) => userIdSet.add(attributes.commenter_id));
            const usersResponse = await fetchUsers({ userId: [...userIdSet] });
            const users = get(usersResponse, 'users', []);
            const usersMap = {};
            users.forEach(user => (usersMap[user.userId] = user));

            comments = comments.map(comment => {
                let commenter = 'Unknown User';
                const commenterId = get(comment, 'attributes.commenter_id');
                if (commenterId in usersMap) {
                    commenter = usersMap[commenterId].fullName;
                }

                const createdAt = get(comment, 'attributes.created_at', '');
                const updatedAt = get(comment, 'attributes.updated_at', '');

                const sortDate = updatedAt || createdAt;

                return { ...comment, commenter, sortDate };
            });

            comments = _orderBy(comments, ['sortDate'], ['desc']);
            setComments(comments);

            return comments;
        } catch (e) {
            setLoading(false);
            return Promise.reject(e);
        } finally {
            setLoading(false);
        }
    }, [fetchComments, fetchUsers, isPartner, partnerId, preppedAnswerId, userId]);

    useEffect(() => {
        async function fetchData() {
            try {
                setLoading(true);
                await getComments();
            } catch (err) {
                setError(err);
            } finally {
                setLoading(false);
            }
        }
        fetchData();
    }, [getComments]);

    async function onClickDeleteComment(id) {
        try {
            setLoading(true);
            await deleteComment({ id });
            await getComments();
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    }

    async function onClickUpdateComment(id, body, type) {
        try {
            setLoading(true);
            const data = {
                data: {
                    data: {
                        id,
                        attributes: {
                            body,
                            communication_type: type.value,
                        },
                        type: 'comments',
                    },
                },
            };
            await updateComment(data);
            await getComments();
        } catch (err) {
            setError(COMMENTS.ERROR.UPDATE);
        } finally {
            setLoading(false);
        }
    }

    async function onSubmit() {
        if (isEmpty(body)) {
            return;
        }

        try {
            setLoading(true);
            const attributes = {
                body,
                communication_type: communicationType.value || 'basic',
            };
            if (isEmpty(preppedAnswerId)) {
                isPartner ? (attributes.partner_id = partnerId) : (attributes.user_id = userId);
            } else {
                attributes.prepped_answer_id = preppedAnswerId;
            }

            await createComment({ data: { attributes, type: 'comments' } });
            await getComments();
            setBody('');
        } catch (err) {
            setError(COMMENTS.ERROR.CREATE);
        } finally {
            setLoading(false);
        }
    }

    function onCommentTypeSelection(communicationType) {
        setCommunicationType(communicationType);
    }

    function isCommentEditAllowed(userId, creatorId) {
        return (
            Acl.check(APP_PERMISSIONS.systemTicketCommentsUpdate) || userId.toString() === creatorId
        );
    }

    function isCommentDeleteAllowed({ creatorId }) {
        let canDelete = false;
        if (isEmpty(preppedAnswerId)) {
            canDelete = isCommentEditAllowed(currentUserId, creatorId);
        } else if (
            canDeletePACommentsAndResources({
                currentUserId: currentUserId.toString(),
                creatorId,
                currentState: preppedAnswerCurrentState,
            })
        ) {
            canDelete = true;
        }

        return canDelete;
    }

    function isCommentUpdateAllowed({ creatorId }) {
        let canUpdate = false;
        if (isEmpty(preppedAnswerId)) {
            canUpdate = isCommentEditAllowed(currentUserId, creatorId);
        } else if (
            canUpdatePACommentsAndResources({
                currentUserId: currentUserId.toString(),
                creatorId,
                currentState: preppedAnswerCurrentState,
            })
        ) {
            canUpdate = true;
        }

        return canUpdate;
    }

    function onChangeBody(e) {
        setBody(e.target.value);
    }

    return {
        body,
        comments,
        communicationType,
        error,
        isCommentDeleteAllowed,
        isCommentUpdateAllowed,
        loading,
        onChangeBody,
        onClickDeleteComment,
        onClickUpdateComment,
        onCommentTypeSelection,
        onSubmit,
    };
}
